/**
 * Push notification service — wraps OneSignal Capacitor plugin with web
 * graceful degradation. On web preview / browser, all methods become no-ops
 * so the app doesn't crash. On native iOS/Android, full OneSignal flow runs.
 */
import { Capacitor } from '@capacitor/core';

const ONESIGNAL_APP_ID = process.env.REACT_APP_ONESIGNAL_APP_ID;

let oneSignalModule = null;
let initialized = false;

const isNative = () => Capacitor.isNativePlatform();

const loadOneSignal = async () => {
  if (!isNative()) return null;
  if (oneSignalModule) return oneSignalModule;
  try {
    const mod = await import('@onesignal/capacitor-plugin');
    oneSignalModule = mod.default || mod.OneSignal || mod;
    return oneSignalModule;
  } catch (e) {
    console.warn('[push] OneSignal plugin not loadable on this platform:', e?.message);
    return null;
  }
};

// ─── Tocco sulla notifica ────────────────────────────────────────────────────
// Il proprietario riceve "Qualcuno è bloccato dalla tua auto", tocca la notifica
// e deve trovarsi davanti al tasto "Sto arrivando", cioè sulla Home. Prima l'app
// riapriva l'ultima schermata visitata: se l'aveva lasciata su Impostazioni,
// trovava Impostazioni — proprio nel momento in cui deve rispondere di fretta.
//
// ⚠️ Il punto delicato: se l'app era CHIUSA, è la notifica stessa a farla partire,
// e il tocco arriva prima che React abbia montato qualsiasi cosa. Un listener
// registrato dentro un componente lo perderebbe. Perciò ci mettiamo in ascolto
// subito all'avvio e, se ancora non c'è nessuno a cui consegnarlo, il tocco resta
// qui in attesa: `onNotificationClick` lo ritira appena l'app è pronta.
let toccoInAttesa = null;
let ascoltatori = [];

const consegnaTocco = (dati) => {
  if (ascoltatori.length > 0) {
    ascoltatori.forEach((fn) => {
      try { fn(dati); } catch (e) { console.warn('[push] ascoltatore fallito:', e?.message); }
    });
  } else {
    toccoInAttesa = dati; // app non ancora pronta: lo teniamo da parte
  }
};

/**
 * Chi vuole reagire al tocco sulla notifica si iscrive qui.
 * Se un tocco era già arrivato prima che l'app fosse pronta, lo riceve subito.
 * Restituisce la funzione per disiscriversi.
 */
export const onNotificationClick = (fn) => {
  ascoltatori.push(fn);
  if (toccoInAttesa !== null) {
    const dati = toccoInAttesa;
    toccoInAttesa = null;
    fn(dati);
  }
  return () => { ascoltatori = ascoltatori.filter((f) => f !== fn); };
};

const ascoltaTocchiNotifica = (OneSignal) => {
  try {
    OneSignal.Notifications.addEventListener('click', (event) => {
      console.log('[push] notifica toccata');
      consegnaTocco(event?.notification?.additionalData || {});
    });
  } catch (e) {
    // Se un domani OneSignal cambiasse nome all'evento, l'app deve continuare a
    // funzionare: si perde l'apertura sulla Home, non le notifiche.
    console.warn('[push] non riesco ad ascoltare il tocco sulla notifica:', e?.message);
  }
};

export const initOneSignal = async () => {
  if (initialized) return;
  if (!ONESIGNAL_APP_ID) {
    console.warn('[push] REACT_APP_ONESIGNAL_APP_ID non configurato — skip init');
    return;
  }
  const OneSignal = await loadOneSignal();
  if (!OneSignal) return;
  try {
    OneSignal.initialize(ONESIGNAL_APP_ID);
    initialized = true;
    ascoltaTocchiNotifica(OneSignal);
    console.log('[push] OneSignal inizializzato');
  } catch (e) {
    console.warn('[push] init fallito:', e?.message);
  }
};

export const requestPushPermission = async () => {
  const OneSignal = await loadOneSignal();
  if (!OneSignal) return false;
  try {
    return await OneSignal.Notifications.requestPermission(true);
  } catch (e) {
    console.warn('[push] requestPermission fallito:', e?.message);
    return false;
  }
};

/**
 * Associa il device OneSignal all'utente loggato.
 * Da chiamare dopo login/register: OneSignal.login(userId) imposta l'external_id
 * sul device. Il backend manda push usando questo external_id.
 */
export const loginPushUser = async (userId) => {
  if (!userId) return;
  const OneSignal = await loadOneSignal();
  if (!OneSignal) return;
  try {
    OneSignal.login(String(userId));
    console.log('[push] OneSignal.login() per user', userId);
  } catch (e) {
    console.warn('[push] login fallito:', e?.message);
  }
};

/** Da chiamare al logout per dissociare il device dall'utente. */
export const logoutPushUser = async () => {
  const OneSignal = await loadOneSignal();
  if (!OneSignal) return;
  try {
    OneSignal.logout();
    console.log('[push] OneSignal.logout()');
  } catch (e) {
    console.warn('[push] logout fallito:', e?.message);
  }
};

/** Ritorna il subscriptionId del device (player_id), null se non disponibile. */
export const getSubscriptionId = async () => {
  const OneSignal = await loadOneSignal();
  if (!OneSignal) return null;
  try {
    return OneSignal.User.pushSubscription.id || null;
  } catch (e) {
    return null;
  }
};

/**
 * Sincronizza il subscriptionId al backend (PATCH /api/users/onesignal).
 * Tenta con retry (l'ID OneSignal può non essere disponibile immediatamente dopo login).
 * Registra anche un listener per cambi futuri di subscription.
 */
export const syncPushSubscription = async (axiosAuth) => {
  if (!isNative() || !axiosAuth) return;
  const OneSignal = await loadOneSignal();
  if (!OneSignal) return;

  const platform = Capacitor.getPlatform();

  const send = async (playerId) => {
    if (!playerId) return;
    try {
      await axiosAuth.patch('/users/onesignal', { playerId, platform });
      console.log('[push] subscriptionId sincronizzato:', playerId);
    } catch (e) {
      console.warn('[push] sync fallito:', e?.message);
    }
  };

  // Tentativo immediato + retry con backoff (OneSignal ID arriva async dopo login)
  for (let attempt = 0; attempt < 5; attempt++) {
    try {
      const id = OneSignal.User.pushSubscription.id;
      if (id) {
        await send(id);
        break;
      }
    } catch (_) { /* ignore */ }
    await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
  }

  // Listener per ID che cambia (es. reinstallazione, opt-in successivo)
  try {
    OneSignal.User.pushSubscription.addEventListener('change', (event) => {
      const newId = event?.current?.id;
      if (newId) send(newId);
    });
  } catch (e) {
    console.warn('[push] addEventListener non supportato:', e?.message);
  }
};
